import React, { Fragment, useState } from 'react';
import { Box, Typography, Modal, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, FormControlLabel, Checkbox } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useToaster } from '../../../../providers/ToasterProvider';
import RadiusButton from '../../../../components/RadiusButton';
import DOWNLOAD_BLUE from '../../../../assets/images/download-blue.svg';
import UPLOAD_GREY from '../../../../assets/images/upload-grey.svg';
import UPLOAD_WHITE from '../../../../assets/images/upload-white.svg';
import DELETE_ICON from '../../../../assets/images/action-delete.svg';
import mimes from '../../../../constant/mimes';
import { uploadProcessingCandidate , downloadCandidateTemplate } from '../../../../services/candidateListService';

const columns = [
    { id: 'index', label: 'Row Number', minWidth: 140 },
    { id: 'rollNo', label: 'Roll No', minWidth: 140 },
    { id: 'userName', label: 'User Name', minWidth: 140 },
    { id: 'errors', label: 'Errors', minWidth: 190 }
];

export const UploadCandidateListModal = ({
    // support new naming `candidateMasterId` and keep old `projectMasterId` for compatibility
    candidateMasterId,
    projectMasterId,
    uploadCandidateModalVisible,
    setUploadCandidateModalVisible,
    uploadCandidateFn,
    candidatesChanged,
    setCandidatesChanged
}) => {
    const [candidateFile, setCandidateFile] = useState(null);
    const [photoFile, setPhotoFile] = useState(null);
    const [signFile, setSignFile] = useState(null);
    const [uploadResult, setUploadResult] = useState({
        isProcessed: false,
        createdCount: 0,
        errors: []
    });
    const { addNotification } = useToaster();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadImages, setUploadImages] = useState(false);
    const uploadCandidateService = uploadCandidateFn;
    const masterId = candidateMasterId ?? projectMasterId;

    const handleFileChange = ({ target }) => {
        const file = target?.files?.[0];
        if (file && file.type === 'text/csv') {
            setCandidateFile(file);
            setUploadResult({ isProcessed: false, errors: [], createdCount: 0 });
        }
    };
    const handlePhotoFileChange = ({ target }) => {
        const file = target?.files?.[0];
        if (file && mimes.ZIP.includes(file.type)) {
            setPhotoFile(file);
            setUploadResult({ isProcessed: false, errors: [], createdCount: 0 });
        }
    };
    const handleSignFileChange = ({ target }) => {
        const file = target?.files?.[0];
        if (file && mimes.ZIP.includes(file.type)) {
            setSignFile(file);
            setUploadResult({ isProcessed: false, errors: [], createdCount: 0 });
        }
    };

    const handleFileRemove = (id) => {
        const element = document.getElementById(id);
        if (element) element.value = '';
        if (id === 'candidateFile') setCandidateFile(null);
        if (id === 'photoFile') setPhotoFile(null);
        if (id === 'signFile') setSignFile(null);
    };

    const closeModal = () => {
        setUploadCandidateModalVisible(false);
        setCandidateFile(null);
        setUploadResult({
            isProcessed: false,
            createdCount: 0,
            errors: [],
        })
        const candidateFileEle = document.getElementById('candidateFile');
        if (candidateFileEle) {
            candidateFileEle.value = '';
        }
    }

    const handleDownloadTemplate = async () => {
        try {
            const res = await downloadCandidateTemplate();
            const url = window.URL.createObjectURL(res);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `candidate-template.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            addNotification('Template downloaded.', 'success');
        } catch (err) {
            console.error(err);
            addNotification('Unexpected error occurred', 'danger');
        }
    };

    const handleUpload = async () => {
        if (uploadImages) {
            if (!candidateFile || !photoFile || !signFile) return;
            setIsUploading(true);
            try {
                const body = new FormData();
                body.append('candidateFile', candidateFile);
                body.append('photoFile', photoFile);
                body.append('signFile', signFile);
                // use uploadCandidate PUT endpoint for direct add + images
                const response = await uploadCandidateService(masterId, body);
                if (response.code === 'OK') {
                    if (response.data?.errors?.length) {
                        setUploadResult({
                            isProcessed: true,
                            createdCount: response.data.createdCount,
                            errors: response.data.errors
                        });
                    } else if (response.data?.createdCount === 0) {
                        addNotification('Unexpected server error occurred.', 'danger');
                        closeModal();
                    } else {
                        addNotification('Candidates added successfully.', 'success');
                        setCandidatesChanged(!candidatesChanged);
                        closeModal();
                    }
                }
            } catch (error) {
                const code = error?.response?.data?.code;
                if (code === 'E_EXAM_ALREADY_ENABLED') {
                    addNotification('Exam enabled for selected master.', 'danger');
                } else {
                    addNotification('Unexpected error occurred.', 'danger');
                }
                console.error('Upload error', error);
            } finally {
                setIsUploading(false);
                handleFileRemove('candidateFile');
                handleFileRemove('photoFile');
                handleFileRemove('signFile');
            }
        } else {
            if (!candidateFile) return;
            setIsUploading(true);
            try {
                const body = new FormData();      
                body.append('file', candidateFile);
                const response = await uploadProcessingCandidate(body);
                if (response.code === 'OK') {
                    if (response.data?.errors?.length) {
                        setUploadResult({
                            isProcessed: true,
                            createdCount: response.data.createdCount,
                            errors: response.data.errors
                        });
                    } else if (response.data?.createdCount === 0) {
                        addNotification('Unexpected server error occurred.', 'danger');
                        closeModal();
                    } else {
                        addNotification('Candidates added successfully.', 'success');
                        setCandidatesChanged(!candidatesChanged);
                        closeModal();
                    }
                }
            } catch (error) {
                const code = error?.response?.data?.code;
                if (code === 'E_EXAM_ALREADY_ENABLED') {
                    addNotification('Exam enabled for selected master.', 'danger');
                } else {
                    addNotification('Unexpected error occurred.', 'danger');
                }
                console.error('Upload error', error);
            } finally {
                setIsUploading(false);
                handleFileRemove('candidateFile');
            }
        }
    };

    const renderStartIcon = () => {
        if (isUploading) return <img src={UPLOAD_WHITE} alt='' />;
        if (uploadImages) {
            return candidateFile && photoFile && signFile ? <img src={UPLOAD_WHITE} alt='' /> : <img src={UPLOAD_GREY} alt='' />;
        }
        return candidateFile ? <img src={UPLOAD_WHITE} alt='' /> : <img src={UPLOAD_GREY} alt='' />;
    };

    const renderTableCell = (columnId, rowIndex) => {
        const row = uploadResult.errors[rowIndex];
        if (!row) return null;
        switch (columnId) {
            case 'index':
                return row['index'];
            case 'rollNo':
                return row['rollNo'];
            case 'userName':
                return row['userName'];
            case 'errors':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        {row['errors'].map((err, i) => <Typography key={i}>{err}</Typography>)}
                    </Box>
                );
            default:
                return row[columnId];
        }
    };

    return (
        <Fragment>
            <Modal open={uploadCandidateModalVisible} onClose={closeModal} hideBackdrop={true} className='backdrop'>
                <Box className='modal-container' sx={{ maxWidth: 900, p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 1 }}>
                        <Typography variant="h6" component="h6" sx={{ color: 'primary.main' }}>
                            Upload Candidate List
                        </Typography>
                        <RadiusButton variant='outlined' startIcon={<img src={DOWNLOAD_BLUE} alt='' />} onClick={handleDownloadTemplate}>
                            Download Template
                        </RadiusButton>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ overflow: 'auto', display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                            <RadiusButton variant='contained' component='label' htmlFor='candidateFile' color='secondary' sx={{ flexShrink: 0 }}>
                                Select Candidates File
                            </RadiusButton>
                            <input style={{ display: 'none' }} type='file' id='candidateFile' onChange={handleFileChange} accept='text/csv' />
                            {candidateFile && candidateFile.name && (
                                <>
                                    <Typography variant='subtitle1'>{candidateFile.name}</Typography>
                                    <img style={{ cursor: 'pointer' }} src={DELETE_ICON} onClick={() => handleFileRemove('candidateFile')} alt='' />
                                </>
                            )}
                        </Box>
                    </Box>

                    <FormControlLabel
                        control={<Checkbox size='small' checked={uploadImages} disabled={true} onChange={() => setUploadImages(!uploadImages)} />}
                        sx={{ maxWidth: 400, '& .MuiTypography-root': { color: 'primary.main', fontSize: 13 } }}
                        label="Upload photo & signature zip files along with CSV"
                    />

                    {uploadImages && (
                        <>
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ overflow: 'auto', display: 'none', alignItems: 'center', gap: 1, pb: 1 }}>
                                    <RadiusButton variant='contained' component='label' htmlFor='photoFile' color='secondary' sx={{ flexShrink: 0 }}>
                                        Select Photo File
                                    </RadiusButton>
                                    <input style={{ display: 'none' }} type='file' id='photoFile' onChange={handlePhotoFileChange} accept={mimes.ZIP} />
                                    {photoFile && photoFile.name && (
                                        <>
                                            <Typography variant='subtitle1'>{photoFile.name}</Typography>
                                            <img style={{ cursor: 'pointer' }} src={DELETE_ICON} onClick={() => handleFileRemove('photoFile')} alt='' />
                                        </>
                                    )}
                                </Box>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ overflow: 'auto', display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                                    <RadiusButton variant='contained' component='label' htmlFor='signFile' color='secondary' sx={{ flexShrink: 0 }}>
                                        Select Signature File
                                    </RadiusButton>
                                    <input style={{ display: 'none' }} type='file' id='signFile' onChange={handleSignFileChange} accept={mimes.ZIP} />
                                    {signFile && signFile.name && (
                                        <>
                                            <Typography variant='subtitle1'>{signFile.name}</Typography>
                                            <img style={{ cursor: 'pointer' }} src={DELETE_ICON} onClick={() => handleFileRemove('signFile')} alt='' />
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </>
                    )}
                    {uploadResult.isProcessed && (
                        <>
                            <Typography sx={{ fontSize: 17, mt: 2, mb: 1 }}>
                                Successfully created records count: {uploadResult.createdCount}
                            </Typography>
                            {uploadResult.errors?.length ? (
                                <>
                                    <Typography sx={{ fontSize: 17, mb: 1 }}>Errors: </Typography>
                                    <Paper sx={{ width: '100%', overflow: 'hidden', bgcolor: 'background.base', maxHeight: 250, borderRadius: 1, mb: 4 }}>
                                        <TableContainer sx={{ px: 1, pb: 1, maxHeight: 'inherit' }}>
                                            <Table stickyHeader aria-label="errors table">
                                                <TableHead>
                                                    <TableRow>
                                                        {columns.map(col => (
                                                            <TableCell key={col.id} align='center' style={{ minWidth: col.minWidth, color: 'primary.main' }}>
                                                                {col.label}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {uploadResult.errors.map((row, idx) => (
                                                        <TableRow key={idx} hover={false}>
                                                            {columns.map(col => (
                                                                <TableCell key={col.id} align='center' sx={{ px: 1.5, py: 0.75 }}>
                                                                    {renderTableCell(col.id, idx)}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                </>
                            ) : null}
                        </>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        <RadiusButton variant='outlined' onClick={closeModal} disabled={isUploading}>Cancel</RadiusButton>
                        <LoadingButton
                            variant='contained'
                            color='secondary'
                            loading={isUploading}
                            onClick={handleUpload}
                            startIcon={renderStartIcon()}
                            disabled={isUploading ? isUploading : uploadImages ? !candidateFile || !photoFile || !signFile : !candidateFile}
                        >
                            Upload
                        </LoadingButton>
                    </Box>
                </Box>
            </Modal>
        </Fragment>
    );
};

export default UploadCandidateListModal;